import React, { useEffect, useMemo, useState } from "react";
import { useTypedSelector } from "store";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { Loader1, Modal } from "components";
import { Button, Stack, Typography } from "@mui/material";
import { setTablesInitialState } from "store/reducers/products/tables/tables.actions";
import { getValidations } from "store/reducers/products/validation/validation.action";
import {
  GroupedValidations,
  GroupAndSortValidations,
  FilterGroupedValidations,
} from "./helpers";
import ValidationList from "./ValidationList.component";
import { ValidationTypes } from "types/validation";
import ValidationFilterCheckbox from "./ValidationFilterCheckbox.component";
import { ValidationModalContext } from "./ValidationModalContext";

export const validationSeverityFilterKey = "validationSeverityFilter";

export default function ValidationModal({
  open,
  closeModal,
}: {
  open: boolean;
  closeModal: () => void;
}) {
  const dispatch = useDispatch();

  //Get all our validations from redux validations state
  const { getting, byId: validationsById } = useTypedSelector(
    (state) => state.products.validation
  );

  // Show all types by default.
  const defaultFilter: Record<ValidationTypes, boolean> = {
    ERROR: true,
    WARNING: true,
    INFO: true,
  };

  const defaultFilterStringified: string = JSON.stringify(defaultFilter);

  // Show all validation severities by default.
  if (!localStorage.getItem(validationSeverityFilterKey)) {
    localStorage.setItem(validationSeverityFilterKey, defaultFilterStringified);
  }

  // Attempt to set validationFilters using localStorage
  const [validationFilters, setValidationFilters] = useState<
    Record<ValidationTypes, boolean>
  >(
    JSON.parse(
      localStorage.getItem("validationSeverityFilter") ??
        defaultFilterStringified
    )
  );

  //Group validations for each object together and sort them for presentation.
  //This is memoized so it refreshes when the validations change.
  const groupedValidations: GroupedValidations = useMemo(
    () => GroupAndSortValidations(validationsById),
    [validationsById]
  );

  // Filter grouped validations based on chosen filters.
  const filteredGroupedValidations: GroupedValidations = useMemo(
    () => FilterGroupedValidations(groupedValidations, validationFilters),
    [groupedValidations, validationFilters]
  );

  useEffect(() => {
    dispatch(setTablesInitialState());
  }, []);

  const handleRefreshValidation = () => {
    //Retrieve current validations
    dispatch(getValidations());
  };

  return (
    <ValidationModalContext.Provider value={true}>
      <Modal
        width={"999px"}
        height={"80vh"}
        open={open}
        closeModal={closeModal}
        title={"Product Line Validation Errors and Warnings"}
      >
        {getting ? (
          <Loader1 />
        ) : (
          <Wrapper>
            <Typography sx={{ pb: "8px" }}>
              {Object.keys(groupedValidations).length > 0
                ? "Before exporting the Product Line, fix any errors below.  Warnings and info will not prevent export."
                : "No validations found."}
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              spacing={3}
              sx={{ pb: "8px" }}
            >
              <ValidationFilterCheckbox
                filterType={ValidationTypes.ERROR}
                label={"Errors"}
                validationFilters={validationFilters}
                setValidationFilters={setValidationFilters}
              />

              <ValidationFilterCheckbox
                filterType={ValidationTypes.WARNING}
                label={"Warnings"}
                validationFilters={validationFilters}
                setValidationFilters={setValidationFilters}
              />

              <ValidationFilterCheckbox
                filterType={ValidationTypes.INFO}
                label={"Info"}
                validationFilters={validationFilters}
                setValidationFilters={setValidationFilters}
              />
            </Stack>

            <ValidationList groupedValidations={filteredGroupedValidations} />

            <RefreshButtonWrapper>
              <Button variant="contained" onClick={handleRefreshValidation}>
                Refresh list
              </Button>
            </RefreshButtonWrapper>
          </Wrapper>
        )}
      </Modal>
    </ValidationModalContext.Provider>
  );
}

const Wrapper = styled.div`
  text-align: left;
  width: 100%;
  height: 100%;
`;

const RefreshButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 32px;
  padding-bottom: 32px;
`;
